import { useCallback, useEffect, useState } from 'react';

import { differenceInDays, parseISO } from 'date-fns';
import { useAtom, useSetAtom } from 'jotai';
import mixpanel from 'mixpanel-browser';
import type { RootStateOrAny } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Userpilot } from 'userpilot';

import { externalPmsSourceAtom } from '../../atoms/externalAtoms';
import { usePreferences } from '../../react-query/usePreferences';
import routes from '../../routes/routes.constants';
import {
  directorPracticeIdAtom,
  useDirectorPracticeView,
} from '../../utils/hooks/useDirectorPracticeView';
import { useTimer } from '../../utils/hooks/useTimer';
import { isExternalIFrame } from '../../utils/iframe';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';

import {
  getAppName,
  hasNoValidLocations,
  isMultiPracticeUser,
} from './login-utils';
import { getPractice, getUser } from './LoginActions';

export const loginChannel = new BroadcastChannel('login');

export const useLogin = () => {
  const { trackEvent } = useTrackEvent();
  const history = useHistory();
  const dispatch = useDispatch();
  const { directorViewPracticeId } = useDirectorPracticeView();
  const setDirectorPracticeId = useSetAtom(directorPracticeIdAtom);
  const [externalPmsSource] = useAtom(externalPmsSourceAtom);
  const isExternalIframeUser = isExternalIFrame();
  const { start } = useTimer();
  const userState = useSelector((state: RootStateOrAny) => state?.login?.user);
  const { data: prefs } = usePreferences();
  const [shouldTrackEvent, setShouldTrackEvent] = useState(false);
  const appLocation = useLocation();
  const isInsightsRoute = appLocation.pathname.includes('insights');

  const handleTrackLogin = useCallback(() => {
    trackEvent(
      mixpanelEvents.login,
      {
        userRole: userState?.userRole,
        applicationName: getAppName(userState?.userRole),
        externalPmsSource,
      },
      undefined,
      undefined,
      undefined,
      undefined,
      userState,
    );
  }, [externalPmsSource, trackEvent, userState]);

  useEffect(() => {
    if (shouldTrackEvent && prefs) {
      handleTrackLogin();
      setShouldTrackEvent(false);
    }
  }, [handleTrackLogin, prefs, shouldTrackEvent]);

  return {
    success: async (jwtToken: string, redirect?: string) => {
      start();
      const user = await getUser(jwtToken)(dispatch);
      const { data: userData } = user;
      const { primaryOrganizationId, primaryPracticeId } = userData;

      Userpilot.identify(userData.id, {
        name: `${userData.firstName || ''} ${userData.lastName || ''}`,
        email: userData.email,
        id: userData.username,
        practice_id: userData.primaryPracticeId,
        practice_name: userData.primaryPracticeName,
        organization_id: userData.primaryOrganizationId,
        organization_name: userData.primaryOrganizationName,
        role: userData.userRole,
        isActiveDDAITrial: userData.practice?.trialDetails?.isActiveDDAITrial,
        isTrialThumbnailShown:
          userData.practice?.trialDetails?.isTrialThumbnailShown,
        daysSinceTrialStart: userData.practice?.trialDetails?.trialStartedOn
          ? differenceInDays(
              new Date(),
              parseISO(userData.practice?.trialDetails?.trialStartedOn),
            )
          : undefined,
        daysTillTrialEnd: userData.practice?.trialDetails?.trialEndsOn
          ? differenceInDays(
              parseISO(userData.practice?.trialDetails?.trialEndsOn),
              new Date(),
            )
          : undefined,
      });

      mixpanel.identify(userData.id);

      if (
        primaryOrganizationId &&
        (directorViewPracticeId || primaryPracticeId)
      ) {
        dispatch(
          getPractice(
            primaryOrganizationId,
            directorViewPracticeId || primaryPracticeId,
            !!directorViewPracticeId,
            jwtToken,
          ),
        );
      }

      // Tracking the login event directly here. We'll follow up in PHOTON-801
      // to ensure that the huddleRedesign preference is defined in the event
      // properties.
      handleTrackLogin();
      // setShouldTrackEvent(true);

      if (
        !redirect &&
        !isExternalIframeUser &&
        isMultiPracticeUser(userData) &&
        !directorViewPracticeId &&
        !isInsightsRoute
      ) {
        history.push(routes.SWITCHBOARD);
      } else if (hasNoValidLocations(userData)) {
        history.push(routes.UNAUTHORIZED);
      } else if (redirect) {
        history.push(redirect);
      }
    },
    loginAllTabs: () => {
      // Skip login channel subscription for external iframe users
      if (isExternalIframeUser) {
        return;
      }

      loginChannel.onmessage = () => {
        // If another tab has authenticated, either through magic link or normal login
        // reload the webpage as we need to rehydrate the user + ensure that we are not viewing previous session
        // We also need to clear the director practice id atom as it may no longer be relevant to the new session
        // This also resolves one of the issues with magic link attempting to use the stale session of the previous tab
        location.assign(location.origin);
        setDirectorPracticeId(null);
      };
    },
  };
};
